import { defineStore } from "pinia";
import { PackingSlip } from "./entity";
import { PackingSlipList } from "./list.gen";
import type { PackingSlipStore, PackingSlipStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const packingSlipList = new PackingSlipList();
export const usePackingSlipStore = defineStore("packingSlipStore", {
  state: (): PackingSlipStoreState => ({
    activeID: null,
    cache: {},
    list: [] as PackingSlip[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-PackingSlip", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.PackingSlip"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(packingSlip: PackingSlip): Promise<void> {
      // Set active
      await packingSlip
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = packingSlip.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(packingSlip: PackingSlip): void {
      const id = packingSlip.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(packingSlip);

        // Set active
        if (this.active === null) {
          this.activeID = packingSlip.id;
        }
      } else {
        this.reinsert(packingSlip);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as PackingSlip[]).map((x: any) => {
        const packingSlip = new PackingSlip({});
        packingSlip.set(x as JSONData);
        return packingSlip;
      });
    },
    async delete(packingSlip: PackingSlip): Promise<void> {
      await packingSlip
        .delete()
        .then(() => {
          this.remove(packingSlip);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const packingSlip = (this.list as PackingSlip[]).find(
        (x: PackingSlip) => x.id === id,
      );
      if (packingSlip !== undefined) {
        await this.delete(packingSlip);
      } else {
        throw new Error("Could not find PackingSlip with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as PackingSlip[]).some((x: PackingSlip) => x.id === id);
    },
    get(id: string): PackingSlip | null {
      return (
        (this.list as PackingSlip[]).find((x: PackingSlip) => x.id === id) ||
        null
      );
    },
    reinsert(packingSlip: PackingSlip): void {
      const index = (this.list as PackingSlip[]).findIndex(
        (x: PackingSlip) => x.id === packingSlip.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          packingSlip,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(packingSlip);
      }
      this.updateKey += 1;
    },
    remove({ id }: PackingSlip): void {
      const index = (this.list as PackingSlip[]).findIndex(
        (x: PackingSlip) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<PackingSlip> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const packingSlip = this.get(id);
        if (packingSlip !== null) {
          return packingSlip;
        }
      }

      // Retrieve packingSlip
      const packingSlip = new PackingSlip({});

      // Get packingSlip
      await packingSlip.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate packingSlip
        await this.activate(entity);
      });

      return packingSlip;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: PackingSlip[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as PackingSlip[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await packingSlipList.getAll().then(async (list) => {
        list.forEach((packingSlip) => {
          this.reinsert(packingSlip);
        });

        // Activate packingSlip
        if (list.length > 0) {
          if (packingSlipList.active !== null) {
            this.activeID = packingSlipList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("packing_slip_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as PackingSlip[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PackingSlip[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as PackingSlip),
        };
      }

      // Retrieve list
      const items = await packingSlipList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((packingSlip) => {
            this.reinsert(packingSlip);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PackingSlip) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PackingSlip[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as PackingSlip),
        };
      }

      // Retrieve list
      const items = await packingSlipList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((packingSlip) => {
            this.reinsert(packingSlip);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PackingSlip) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PackingSlip[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as PackingSlip),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await packingSlipList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((packingSlip) => {
            this.reinsert(packingSlip);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PackingSlip) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(packingSlip: PackingSlip): Promise<string> {
      // Update packingSlip
      await packingSlip.update();

      // Reinsert packingSlip
      this.reinsert(packingSlip);

      // Activate packingSlip
      if (this.active === null) {
        await this.activate(packingSlip);
      }

      this.emitStoreUpdateEvent();

      return packingSlip.id;
    },
  },
  getters: {
    active(): PackingSlip | null {
      if (this.activeID === null) {
        return null;
      }

      const packingSlip = (this.list as PackingSlip[]).find(
        (x: PackingSlip) => x.id === this.activeID,
      );
      if (packingSlip === undefined) {
        return null;
      }

      return packingSlip;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createPackingSlipStore(): PackingSlipStore {
  const store = usePackingSlipStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as PackingSlipStore; // TODO: Fix this
}
